import React, { useState, useEffect } from "react";
import { apiGet } from "../../utils/api";
import {
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaSpinner,
  FaSync,
  FaChevronDown,
  FaChevronRight,
} from "react-icons/fa";

const STATUS_CONFIG = {
  completed: { icon: FaCheckCircle, color: "text-green-500", bg: "bg-green-50", label: "Completed" },
  running: { icon: FaSpinner, color: "text-blue-500", bg: "bg-blue-50", label: "Running" },
  delayed: { icon: FaClock, color: "text-amber-500", bg: "bg-amber-50", label: "Waiting" },
  waiting_for_reply: { icon: FaClock, color: "text-amber-500", bg: "bg-amber-50", label: "Waiting for Reply" },
  ai_conversation: { icon: FaClock, color: "text-violet-500", bg: "bg-violet-50", label: "AI Conversation" },
  errored: { icon: FaTimesCircle, color: "text-red-500", bg: "bg-red-50", label: "Errored" },
  pending: { icon: FaClock, color: "text-ink-muted", bg: "bg-surface-bg", label: "Pending" },
};

const NODE_LABELS = {
  trigger: "Trigger",
  sendMessage: "Send Message",
  delay: "Delay",
  condition: "Condition",
  sendToAI: "Send to AI",
  triggerWebhook: "Webhook",
  shopifyTrigger: "Shopify Event",
  waitForReply: "Wait for Reply",
  aiConversation: "AI Conversation",
  aiAgent: "AI Agent",
  // AI agent run-log actions (not node types — shown in the step list).
  ai_agent_turn: "AI Agent reply",
  ai_agent_paused: "AI standing by — human took over",
  ai_handoff: "Handed off to a human",
  ai_conversation_deferred: "AI reply deferred by send pacing",
  loop: "Loop",
  sendVoiceNote: "Voice Note",
  httpRequest: "HTTP Request",
};

function isError(result) {
  return typeof result === "string" && result.startsWith("error:");
}

function StepResult({ result }) {
  if (!result) return <span className="text-ink-faint text-2xs">—</span>;
  if (isError(result)) {
    return <span className="text-red-600 text-2xs font-mono break-all">{result}</span>;
  }
  return <span className="text-green-700 text-2xs font-mono">{result}</span>;
}

function ExecutionRow({ exec, nodes }) {
  const [open, setOpen] = useState(false);
  const log = Array.isArray(exec.log) ? exec.log : [];
  // Fallback to "Pending" — never to "Errored" — so an unknown/in-flight status
  // doesn't render the row in red as if the run had failed.
  const status = STATUS_CONFIG[exec.status] || STATUS_CONFIG.pending;
  const StatusIcon = status.icon;
  const hasError = log.some((step) => isError(step.result));
  const contactShort = (exec.contactJid || "").split("@")[0];
  const time = new Date(exec.createdAt).toLocaleString();

  // Find which step failed
  const failedStep = log.find((step) => isError(step.result));

  return (
    <div className="border border-surface-border rounded-lg overflow-hidden">
      {/* Summary row */}
      <button
        className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-surface-bg transition-colors ${open ? "bg-surface-bg" : ""}`}
        onClick={() => setOpen(!open)}
      >
        {open ? <FaChevronDown size={9} className="text-ink-muted" /> : <FaChevronRight size={9} className="text-ink-muted" />}
        <StatusIcon size={12} className={`${status.color} ${exec.status === "running" ? "animate-spin" : ""}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-ink-primary truncate">
              {contactShort || "Unknown"}
            </span>
            <span className={`text-2xs px-1.5 py-0.5 rounded-full font-medium ${status.bg} ${status.color}`}>
              {status.label}
            </span>
            {hasError && !isError(log[log.length - 1]?.result) && (
              <span className="text-2xs px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600 font-medium">
                Partial
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-2xs text-ink-faint">{time}</span>
            {exec.triggerMessage && (
              <span className="text-2xs text-ink-muted truncate max-w-[200px]">
                "{exec.triggerMessage}"
              </span>
            )}
            {failedStep && (
              <span className="text-2xs text-red-500 truncate max-w-[200px]">
                Failed at: {NODE_LABELS[failedStep.action] || failedStep.action}
              </span>
            )}
          </div>
        </div>
        <span className="text-2xs text-ink-faint">{log.length} steps</span>
      </button>

      {/* Expanded step log */}
      {open && (
        <div className="border-t border-surface-border bg-surface-bg/50">
          {log.length === 0 ? (
            <p className="px-4 py-3 text-xs text-ink-muted">No steps recorded.</p>
          ) : (
            <div className="divide-y divide-surface-border">
              {log.map((step, i) => {
                const nodeLabel = NODE_LABELS[step.action] || step.action;
                const stepError = isError(step.result);
                return (
                  <div key={i} className={`flex items-start gap-3 px-4 py-2 ${stepError ? "bg-red-50/50" : ""}`}>
                    {/* Step number */}
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center text-2xs font-bold flex-shrink-0 mt-0.5 ${
                      stepError ? "bg-red-100 text-red-600" : "bg-brand/10 text-brand"
                    }`}>
                      {i + 1}
                    </div>

                    {/* Step details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-ink-primary">{nodeLabel}</span>
                        <span className="text-2xs text-ink-faint font-mono">{step.nodeId}</span>
                      </div>
                      <StepResult result={step.result} />
                      {step.responseStatus && (
                        <span className="text-2xs text-ink-muted ml-1">HTTP {step.responseStatus}</span>
                      )}
                      {step.responseSnippet && (
                        <p className="text-2xs text-ink-faint font-mono mt-0.5 truncate max-w-[300px]">{step.responseSnippet}</p>
                      )}
                    </div>

                    {/* Timestamp */}
                    <span className="text-2xs text-ink-faint flex-shrink-0">
                      {new Date(step.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function FlowHistory({ flowId }) {
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchExecutions = async () => {
    setLoading(true);
    try {
      const res = await apiGet(`/api/autoflows/${flowId}/executions`);
      setExecutions(res?.data || []);
    } catch {
      setExecutions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (flowId) fetchExecutions();
  }, [flowId]);

  return (
    <div className="w-full md:w-80 border-l border-surface-border bg-surface-card flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-surface-border">
        <h3 className="text-sm font-semibold text-ink-primary">Execution History</h3>
        <button
          className="p-1.5 text-ink-muted hover:text-ink-primary rounded-md hover:bg-surface-bg"
          onClick={fetchExecutions}
          title="Refresh"
        >
          <FaSync size={11} />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <FaSpinner className="animate-spin text-ink-muted" size={16} />
          </div>
        ) : executions.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-xs text-ink-muted">No executions yet.</p>
            <p className="text-2xs text-ink-faint mt-1">
              Trigger the flow by sending a matching message.
            </p>
          </div>
        ) : (
          executions.map((exec) => (
            <ExecutionRow key={exec.id} exec={exec} />
          ))
        )}
      </div>

      {/* Footer summary */}
      {executions.length > 0 && (
        <div className="px-4 py-2 border-t border-surface-border flex items-center justify-between">
          <span className="text-2xs text-ink-faint">{executions.length} executions</span>
          <div className="flex items-center gap-2">
            <span className="text-2xs text-green-600">
              {executions.filter((e) => e.status === "completed").length} passed
            </span>
            <span className="text-2xs text-red-500">
              {executions.filter((e) => e.status === "errored" || (e.log || []).some((s) => isError(s.result))).length} failed
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
